import { useAtom } from 'jotai';
import _ from 'lodash';
import { ChangeEvent, useMemo } from 'react';

import { Option } from '@/lib/v2/components';

import {
  atomConditionsValues,
  ConditionState,
} from '@/src/modules/AutomationModuleNew/atoms/flows';

import { useGetSelectsBinaryConditionalOptions } from './useGetSelectsBinaryConditionalOptions';

import { Field } from '@/modules/ContactsModule/types/Segments';

export const useBinaryConditional = (index: number = 0) => {
  const [conditionsValues, setConditionsValues] = useAtom(atomConditionsValues);
  const { countryOptions, genderOptions, selectTypesOptions, getSelectConditionsOptions } =
    useGetSelectsBinaryConditionalOptions();

  const selectConditionOptions = useMemo<Option[]>(() => {
    const currentConditionState = conditionsValues[index];
    return getSelectConditionsOptions(
      currentConditionState.selectTypeValue,
      currentConditionState.selectCustomFieldsValue
    );
  }, [conditionsValues, getSelectConditionsOptions, index]);

  const optionsCustomFields = useMemo<Option[]>(() => {
    const currentConditionState = conditionsValues[index];
    if (!currentConditionState) return [];
    if (
      currentConditionState.selectCustomFieldsValue?.data.metadata.optionType &&
      currentConditionState.selectCustomFieldsValue.data.metadata?.defaultValues
    ) {
      return currentConditionState.selectCustomFieldsValue.data.metadata?.defaultValues?.map(
        (value) => ({
          id: value,
          name: value,
          value,
        })
      );
    }
    return [];
  }, [conditionsValues, index]);

  const handleSelectTypeChange = (option: Option | Option[]) => {
    if (Array.isArray(option)) return;

    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectTypeValue: option,
        selectBelongValue: undefined,
        selectIdsValues: [],
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  const handleSelectCustomFieldsChange = (option: Option | Option[]) => {
    if (Array.isArray(option)) return;
    const optionValue = option as Option & { data: Field };
    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectCustomFieldsValue: optionValue,
        selectBelongValue: undefined,
        selectIdsValues: [],
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  const handleSelectBelongChange = (option: Option | Option[]) => {
    if (Array.isArray(option)) return;

    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectBelongValue: option,
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  const handleSelectIdsChange = (option: Option | Option[]) => {
    if (!Array.isArray(option)) return;

    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectIdsValues: option,
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  const handleMultiInputChange = (values: string[]) => {
    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const type = newConditions[index].selectCustomFieldsValue?.data.type;
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectIdsValues: values.map((value) => ({
          id: value,
          name: value,
          value: type === 'number' ? Number(value) : value,
        })),
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  const handleOptionsCustomFieldsChange = (option: Option | Option[]) => {
    if (!Array.isArray(option)) return;
    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectIdsValues: option,
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  const handleInputNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConditionsValues((prev) => {
      const newConditions = _.cloneDeep(prev);
      const type = newConditions[index].selectCustomFieldsValue?.data.type;
      const newCondition: ConditionState = {
        ...newConditions[index],
        selectIdsValues: [
          { id: value, name: value, value: type === 'number' ? Number(value) : value },
        ],
      };
      newConditions[index] = newCondition;
      return newConditions;
    });
  };

  return {
    handleSelectTypeChange,
    handleSelectCustomFieldsChange,
    handleSelectBelongChange,
    handleSelectIdsChange,
    handleMultiInputChange,
    handleOptionsCustomFieldsChange,
    handleInputNumberChange,
    selectTypesOptions,
    selectConditionOptions,
    conditionsValues,
    optionsCustomFields,
    genderOptions,
    countryOptions,
  };
};
